import { GeoJSON } from "react-leaflet";
import * as turf from "@turf/turf";
import { useMemo } from "react";
import L from "leaflet";
// ─── CONFIG ──────────────────────────────────────────────────────────────────
const GRID_STEP_KM = 15;          // cell size in km
const INITIAL_ENERGY = 1.0;       // starting wave energy (0–1)
const ENERGY_DECAY = 0.04;        // energy lost per BFS step
const MIN_ENERGY = 0.05;          // wave dies below this threshold
// ─────────────────────────────────────────────────────────────────────────────

export const TsunamiOverlay = ({ center, tsunami, landPolygons }) => {
  const { coastlineHits, waveFront } = useMemo(() => {
    if (!tsunami || tsunami.radius_km <= 0 || !landPolygons || !center) {
      return { coastlineHits: [], waveFront: null };
    }

    const [lat, lon] = center;

    // ── 1. Build a spatial index of land polygons ──────────────────────────
    // Pre-filter to only polygons within a loose bounding box so isOnLand()
    // doesn't check the entire world on every cell.
    const maxReach = ((INITIAL_ENERGY - MIN_ENERGY) / ENERGY_DECAY) * GRID_STEP_KM;
    const searchCircle = turf.circle([lon, lat], maxReach, { units: "kilometers", steps: 32 });
    const searchBbox = turf.bbox(searchCircle);

    const nearbyLand = (landPolygons.features || landPolygons).filter((f) => {
      if (!f?.geometry?.coordinates?.length) return false;
      try {
        return bboxOverlap(searchBbox, turf.bbox(f));
      } catch {
        return false;
      }
    });

    // ── 2. Fast point-in-land test ─────────────────────────────────────────
    function isOnLand(ptLon, ptLat) {
      const pt = turf.point([ptLon, ptLat]);
      for (const f of nearbyLand) {
        try {
          if (turf.booleanPointInPolygon(pt, f)) return true;
        } catch {}
      }
      return false;
    }

    // ── 3. Convert km offsets → [lon, lat] neighbours ─────────────────────
    // We move in 8 directions. The lon/lat deltas are approximated from km.
    const LAT_DEG_PER_KM = 1 / 111.32;
    function neighbours(cellLon, cellLat) {
      const lonDegPerKm = LAT_DEG_PER_KM / Math.cos((cellLat * Math.PI) / 180);
      const d = GRID_STEP_KM;
      return [
        [cellLon,              cellLat + d * LAT_DEG_PER_KM ],  // N
        [cellLon + d * lonDegPerKm, cellLat + d * LAT_DEG_PER_KM ],  // NE
        [cellLon + d * lonDegPerKm, cellLat              ],  // E
        [cellLon + d * lonDegPerKm, cellLat - d * LAT_DEG_PER_KM ],  // SE
        [cellLon,              cellLat - d * LAT_DEG_PER_KM ],  // S
        [cellLon - d * lonDegPerKm, cellLat - d * LAT_DEG_PER_KM ],  // SW
        [cellLon - d * lonDegPerKm, cellLat              ],  // W
        [cellLon - d * lonDegPerKm, cellLat + d * LAT_DEG_PER_KM ],  // NW
      ];
    }

    // ── 4. BFS flood fill from impact point ───────────────────────────────
    // Each queue entry: { lon, lat, energy }
    // visited key: rounded grid coords to avoid re-processing same cell
    const visited = new Map();   // key → energy at arrival
    const coastHits = [];        // cells where water→land transition detected
    const waveCells = [];        // all reached water cells (for visualisation)

    const key = (lo, la) =>
      `${Math.round(lo / (GRID_STEP_KM * LAT_DEG_PER_KM))}:${Math.round(la / (GRID_STEP_KM * LAT_DEG_PER_KM))}`;

    // Impact point must be in water — if it's on land abort gracefully
    if (isOnLand(lon, lat)) {
      return { coastlineHits: [], waveFront: null };
    }

    const queue = [{ lon, lat, energy: INITIAL_ENERGY }];
    visited.set(key(lon, lat), INITIAL_ENERGY);

    while (queue.length > 0) {
      const { lon: cLon, lat: cLat, energy } = queue.shift();   // BFS = FIFO
      waveCells.push([cLon, cLat, energy]);

      const nextEnergy = energy - ENERGY_DECAY;
      if (nextEnergy <= MIN_ENERGY) continue;  // wave too weak — stop here

      for (const [nLon, nLat] of neighbours(cLon, cLat)) {
        const k = key(nLon, nLat);
        if (visited.has(k)) continue;
        visited.set(k, nextEnergy);

        if (isOnLand(nLon, nLat)) {
          // Water→land edge: this IS a coastline hit
          // Record the midpoint between current water cell and land cell
          coastHits.push({
            lon: (cLon + nLon) / 2,
            lat: (cLat + nLat) / 2,
            energy,               // energy at the moment of hitting coast
          });
          // Don't enqueue land cells — wave doesn't propagate through land
        } else {
          queue.push({ lon: nLon, lat: nLat, energy: nextEnergy });
        }
      }
    }

    // ── 5. Build GeoJSON from coastline hits ──────────────────────────────
    // Group hits by energy level into a FeatureCollection so we can colour
    // by wave intensity.
    const hitFeatures = coastHits.map((h) =>
      turf.point([h.lon, h.lat], { energy: h.energy })
    );

    const waveFrontFeature = waveCells.length > 0
      ? turf.featureCollection(
          waveCells.map(([lo, la, e]) => turf.point([lo, la], { energy: e }))
        )
      : null;

    return {
      coastlineHits: hitFeatures,
      waveFront: waveFrontFeature,
    };
  }, [center?.[0], center?.[1], tsunami?.radius_km, landPolygons]);

  const hitEnergies = coastlineHits.map(f => f.properties.energy);
  const minE = Math.min(...hitEnergies);
  const maxE = Math.max(...hitEnergies);
  const energyRange = maxE - minE || 1;

  // ── 6. Style by energy: brighter = more energetic hit ─────────────────
const hitStyle = (feature) => {
  const raw = feature?.properties?.energy ?? 0.5;
  const e = (raw - minE) / energyRange;  // normalized 0→1 across actual range

  const r = 0;
  const g = Math.round((1 - e) * 255);
  const b = Math.round(e * 255);

  return {
    radius: 4 + e * 6,
    fillColor: `rgb(${r},${g},${b})`,
    color: "#000",
    weight: 0.5,
    fillOpacity: 0.85,
  };
};

  return (
    <>
      {/* Wave propagation cells (debug/visualisation — remove if noisy) */}
      {waveFront && (
        <GeoJSON
          key="wavefront"
          data={waveFront}
          pointToLayer={(f, latlng) => {
            const e = f.properties.energy;
            return L.circleMarker(latlng, {
              radius: 2,
              fillColor: `rgba(0,120,255,${e * 0.3})`,
              color: "none",
              fillOpacity: e * 0.3,
            });
          }}
        />
      )}

      {/* Coastline impact hits */}
      {coastlineHits.length > 0 && (
        <GeoJSON
          key={`hits-${coastlineHits.length}`}
          data={turf.featureCollection(coastlineHits)}
          pointToLayer={(f, latlng) => {
            const style = hitStyle(f);
            return L.circleMarker(latlng, style);
          }}
        />
      )}
    </>
  );
};

function bboxOverlap(bbox1, bbox2) {
  return !(
    bbox1[2] < bbox2[0] ||
    bbox1[0] > bbox2[2] ||
    bbox1[3] < bbox2[1] ||
    bbox1[1] > bbox2[3]
  );
}